import { markAccountEndUnloading, clearAccountEndUnloading } from '@papan/core-ui/ui/sessionEnded'
import { revokeShareVisit } from './shareVisitRevoke'

/**
 * #1316 review round 1 (owner, final) — the ONE full page load at an ACCOUNT end: a sign-out, signed out in another
 * tab, another account, a session that died. The page is the old account's: its in-flight requests and retries, its
 * timers, its react-query cache, its open editors' typed text. A full load drops all of it at once, so nothing of the
 * previous account reaches the sign-in page or the next account (P10) and nothing is left loading or half-done.
 *
 * Called only by `useAuth` once the auth state has applied — after the server sign-out resolved (a load before it
 * would keep the cookie and sign the person straight back in), or once the re-check named the new / missing user.
 * Never from an `endSession` listener: those run before the cookie is cleared. Same-person ends (an identity or tenant
 * switch, a share-session change) do not come here.
 */

/** #1316 review round 2 (owner, final 2026-10-28) — the share view's link front door (`/share/<token>`), registered by
 *  the share chrome once its `/info` names the link (`useAccountEndShareFrontDoor`). An account end on the share view
 *  loads it, so the visit restarts at the link's own door under whoever is signed in now (or anonymously): a public
 *  link mints a fresh session, a restricted one admits again or asks its gate. */
let shareFrontDoor: string | null = null

/** #1316 review round 2 (H2b) — an account end on the share view that came before the chrome knew its front door: it
 *  waits for the registration (`setAccountEndShareFrontDoor`) rather than reloading the old session's URL. */
let pendingShareAccountEnd: { go: (target: string) => void; timer: ReturnType<typeof setTimeout> } | null = null

/** The share chrome holds its link's front door while it is mounted (`null` = none known). Returns the release. A held
 *  account end leaves for it now. */
export function setAccountEndShareFrontDoor(href: string | null): () => void {
  shareFrontDoor = href
  if (href && pendingShareAccountEnd) {
    const { go, timer } = pendingShareAccountEnd
    pendingShareAccountEnd = null
    clearTimeout(timer)
    go(href)
  }
  return () => { if (shareFrontDoor === href) shareFrontDoor = null }
}

/** `accountEndReloadTarget` on the share view before the chrome knows its front door: not a URL — the account end is
 *  held until the front door is registered (`reloadAfterAccountEnd`). */
export const SHARE_FRONT_DOOR_PENDING = '#share-front-door-pending'
/** How long a held account end waits for the front door; then it leaves for the sign-in page — never back into the old
 *  session (`SHARE_ACCOUNT_END_FALLBACK`). */
export const SHARE_FRONT_DOOR_WAIT_MS = 5000
export const SHARE_ACCOUNT_END_FALLBACK = '/login'
/** The share session this page shows (`/share-view/<sid>`), or null off the share view. */
function shareViewSessionId(): string | null {
  const m = /^\/share-view\/([^/?#]+)/.exec(window.location.pathname)
  if (!m) return null
  try { return decodeURIComponent(m[1]) } catch { return null }
}

/** THE one place that decides where an involuntary account end reloads. Off the share view: the page it was on — the
 *  app then decides as on any load (the login guard carries `?next=` for a signed-out visitor; another account lands
 *  where it may). On the share view: the link's front door; before the chrome knows it (its `/info` not in yet)
 *  `SHARE_FRONT_DOOR_PENDING` — never the share page itself: reloading `/share-view/<sid>` would re-enter the old
 *  account's still-live, browser-bound visit as whoever is signed in now (P10, #989). */
export function accountEndReloadTarget(): string | null {
  if (typeof window === 'undefined') return null
  const { pathname, search } = window.location
  if (pathname.startsWith('/share-view/')) return shareFrontDoor ?? SHARE_FRONT_DOOR_PENDING
  // No `#fragment`: an assign to the same URL plus a hash only scrolls to the anchor — no load, the old page stays.
  return pathname + search
}

/** How long the unload mark may stand when no navigation started. `beforeunload` fires synchronously as the load
 *  begins, so once this passes nothing will ask the old page to prompt for THIS load. */
const UNLOAD_MARK_MS = 2000

// ── Two full page leaves never race (#1316 review round 2) ──────────────────────────────────────────────────────────
// On the share view an account end and the share view's own leave (`leaveShareView`: a Log out, a revoke confirmed by
// its probe, a sign-out in another tab) can both start for one event. Whichever starts first wins: the other is a
// no-op for `PAGE_LEAVE_WINDOW_MS` — long enough for one navigation to commit, short enough that a leave the browser
// never made (a "Leave site?" answered Stay) doesn't block the next.
const PAGE_LEAVE_WINDOW_MS = 2000
let pageLeave: { kind: 'account-end' | 'share'; at: number } | null = null

/** Claim the full page leave for `kind`: false when the OTHER kind started one within the window (that one wins). */
export function claimPageLeave(kind: 'account-end' | 'share'): boolean {
  const now = Date.now()
  if (pageLeave && pageLeave.kind !== kind && now - pageLeave.at < PAGE_LEAVE_WINDOW_MS) return false
  pageLeave = { kind, at: now }
  return true
}

/** Tests: a page whose mocked navigation never unloads starts the next test with no leave held. */
export function resetPageLeaveForTests(): void {
  pageLeave = null
}

/** The full page load. Never throws (a non-browser host has nothing to reload); a no-op when the share view's own leave
 *  already started (`claimPageLeave`). Marked first, so no "Leave site?" of the old account's (a dirty editor tab,
 *  `beforeUnloadPrompt`) can cancel it — G1 review I-1. A target that is the current URL is a `reload()` (an `assign`
 *  to it is not guaranteed to load). If nothing navigated (the page is still here after `UNLOAD_MARK_MS`) the mark is
 *  cleared, so a later dirty-tab prompt works as usual.
 *
 *  On the share view (#1316 review round 2, H2b) the old account's VISIT is ended first — the share view's own Log out
 *  revoke (#809) — and the target REPLACES the page, so neither Back nor a reload re-enters `/share-view/<A's sid>`
 *  as whoever is signed in now. `SHARE_FRONT_DOOR_PENDING` holds the leave until the chrome registers the front door,
 *  at most `SHARE_FRONT_DOOR_WAIT_MS`; then it leaves for the sign-in page. */
export function reloadAfterAccountEnd(target: string): void {
  if (typeof window === 'undefined') return
  const sid = shareViewSessionId()
  if (sid) {
    // #1316 codex review (P10): the old account's share view is still painted until the browser leaves — blank it NOW,
    // synchronously, before any wait (the held end below, the navigation itself), so the next person never sees it.
    blankPageForAccountEnd()
    if (target !== SHARE_FRONT_DOOR_PENDING) { endShareVisitThenReplace(sid, target); return }
    if (pendingShareAccountEnd) return   // one held leave at a time
    const timer = setTimeout(() => {
      if (pendingShareAccountEnd?.timer !== timer) return
      pendingShareAccountEnd = null
      // #1316 review round 3 (H2b m1): the person left the share view in the SPA while it waited (the chrome unmounted
      // before it ever registered a front door) — no revoke / `/login` from a page that is not the share view; the end
      // is the ordinary one for the page they are on now.
      if (shareViewSessionId() !== sid) { unblankPage(); loadPage(accountEndReloadTarget() ?? '/'); return }
      endShareVisitThenReplace(sid, SHARE_ACCOUNT_END_FALLBACK)
    }, SHARE_FRONT_DOOR_WAIT_MS)
    pendingShareAccountEnd = { go: (frontDoor) => { endShareVisitThenReplace(sid, frontDoor) }, timer }
    return
  }
  loadPage(target)
}

/** The ordinary (non-share) account-end load: claimed, marked, then `reload()` / `assign`. */
function loadPage(target: string): void {
  if (!claimPageLeave('account-end')) return
  markAccountEndUnloading()
  try {
    const { pathname, search } = window.location
    if (target === pathname + search) window.location.reload()
    else window.location.assign(target)
  } catch { /* non-browser */ }
  setTimeout(clearAccountEndUnloading, UNLOAD_MARK_MS)
}

/** The share view's account-end leave: claimed at the moment it leaves (a share leave that started first wins, and then
 *  nothing is sent), the visit revoked, then `location.replace` — in the same task. #1316 codex review (P10): the revoke
 *  is `keepalive` and NOT awaited — a keepalive request outlives the page, so nothing of the old account's share view
 *  waits on screen for it. */
function endShareVisitThenReplace(sid: string, target: string): void {
  if (!claimPageLeave('account-end')) return
  markAccountEndUnloading()
  void revokeShareVisit(sid, { keepalive: true })
  try { window.location.replace(target) } catch { /* non-browser */ }
  setTimeout(clearAccountEndUnloading, UNLOAD_MARK_MS)
}

// ── The old page is blanked at a share-view account end (#1316 codex review, P10) ──────────────────────────────────
// Inline `display: none !important` on <body> (and the tab title cleared): no stylesheet or portal child can show any
// of it again, and React keeps running underneath (the chrome must still register its front door during a hold).
const BLANK_ATTR = 'data-account-end-blank'

/** Hide everything the old page shows, synchronously. Idempotent. */
export function blankPageForAccountEnd(): void {
  if (typeof document === 'undefined' || !document.body) return
  document.body.setAttribute(BLANK_ATTR, '')
  document.body.style.setProperty('display', 'none', 'important')
  document.title = ''
}

/** Show the page again — only when the held end turned out to be an ordinary load of another page (and tests). */
export function unblankPage(): void {
  if (typeof document === 'undefined' || !document.body?.hasAttribute(BLANK_ATTR)) return
  document.body.removeAttribute(BLANK_ATTR)
  document.body.style.removeProperty('display')
}

/** Tests: no held share-view account end carries into the next test. */
export function resetShareAccountEndForTests(): void {
  if (pendingShareAccountEnd) clearTimeout(pendingShareAccountEnd.timer)
  pendingShareAccountEnd = null
  unblankPage()
}
